// src/pages/OTPVerificationContainer.jsx

import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { AXIOS_POST } from "../config/axios";
import Loader from "../helper/Loader";
import { useNavigate, useLocation } from "react-router-dom";
import ErrorBox from "../helper/Messages/Error";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Translate from "../config/Translate";
import { useSelector } from "react-redux";
import { selectLanguage } from "../features/LanguageSlice";
import { companyInfo } from "../helper/Company";
import LanguageSwitch from "./languageSwitch";
import En from "../helper/Dictionary/En";
import Fr from "../helper/Dictionary/Fr";
const OTPVerificationContainer = () => {
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [showLoader, setShowLoader] = useState(false);
  const [errors, setErrors] = useState(null);
  const [canResend, setCanResend] = useState(false);
 const [countdown, setCountdown] = useState(60);
 
  
  const navigate = useNavigate();
  const location = useLocation();
  const inputRefs = useRef([]);
  const token = JSON.parse(localStorage.getItem("token"))?.access_token;
  const languageValue = useSelector(selectLanguage);
  const cinfo = companyInfo();


// Get placeholders based on language
const pOTPPlaceholder =
  languageValue === "En"
    ? En["page.otpPlaceholder"]
    : Fr["page.otpPlaceholder"];

  // Get verification type and contact info from navigation state or localStorage
  const verificationType = location.state?.verificationType || 'email';
  const phoneNumber = location.state?.phoneNumber || localStorage.getItem("verificationPhone");
  const email = localStorage.getItem("verificationEmail");
  
  // 🔥 NEW: Get OTP data from navigation state
  const otpData = location.state?.otpData || null; // This contains the full result.data from send-sms-otp
  const otpKey = otpData?.otp_key || localStorage.getItem("verificationOtpKey");
   const [currentOtpKey, setCurrentOtpKey] = useState(
    otpData?.otp_key || localStorage.getItem("verificationOtpKey")
  );
  const contactInfo = verificationType === 'phone' ? phoneNumber : email;

// Function to get latest OTP info from backend
const getLatestOtpInfo = async () => {
  const userEmail = localStorage.getItem("userEmail");
  if (!userEmail || verificationType !== 'phone') return null;

  try {
    const result = await AXIOS_POST(
      "/get-latest-otp",
      { email: userEmail },
      token,
      languageValue === "En" ? "en" : "fr"
    );

    if (result.data?.success && result.data?.data) {
      const otpData = result.data.data;
      setCurrentOtpKey(otpData.otp_key);
      localStorage.setItem("verificationOtpKey", otpData.otp_key);
      return otpData;
    }
  } catch (error) {
    console.error('Failed to get latest OTP info:', error);
  }
  return null;
};

  // Store otp_key in localStorage for persistence
  useEffect(() => {
    if (currentOtpKey) {
      localStorage.setItem("verificationOtpKey", currentOtpKey);
    }
  }, [currentOtpKey]);

  // Countdown timer for resend functionality
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [countdown]);

  // Auto-focus first input on mount
  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  const handleInputChange = (index, value) => {
    // Only allow digits
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setErrors(null);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    
    // Handle paste
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const paste = e.clipboardData.getData('text');
    const digits = paste.replace(/\D/g, '').slice(0, 6);
    
    if (digits.length > 0) {
      const newOtp = [...otp];
      for (let i = 0; i < digits.length && i < 6; i++) {
        newOtp[i] = digits[i];
      }
      setOtp(newOtp);
      
      // Focus the next empty input or the last one
      const nextIndex = Math.min(digits.length, 5);
      inputRefs.current[nextIndex]?.focus();
    }
  };

  const handleSubmit = async () => {
    setErrors(null);
    
    const otpString = otp.join('');
    
    if (otpString.length !== 6) {
      languageValue === "En"
        ? setErrors("Please enter the complete 6-digit code")
        : setErrors("Veuillez saisir le code à 6 chiffres complet");
      return;
    }
        if (verificationType === 'phone') {
      const latestOtpInfo = await getLatestOtpInfo();
      if (!latestOtpInfo || !latestOtpInfo.otp_key) {
        setErrors("OTP session expired. Please request a new OTP.");
        return;
      }
    }


    setShowLoader(true);
    
    // API endpoint based on verification type
    const VERIFY_OTP_ENDPOINT = verificationType === 'phone' 
      ? "/verify-sms-otp" 
      : "/api/verify-email-otp";
    
    // 🔥 UPDATED: Request data with otp_key for phone verification
    const userEmail = localStorage.getItem("userEmail");
    const requestData = verificationType === 'phone'
      ? { 
          phone: phoneNumber, 
          otp_code: otpString,
          otp_key: currentOtpKey, // Use the latest OTP key
          email: userEmail
        }
      : { email: email, otp: otpString };

    console.log('Sending verification request:', requestData); // Debug log

    AXIOS_POST(
      VERIFY_OTP_ENDPOINT,
      requestData,
      token,
      languageValue === "En" ? "en" : "fr"
    )
      .then((result) => {
        setShowLoader(false);
        languageValue === "En"
          ? toast.success("Verification successful!")
          : toast.success("Vérification réussie!");
        
        // Clear stored verification data
        localStorage.removeItem("verificationPhone");
        localStorage.removeItem("verificationEmail");
        localStorage.removeItem("verificationOtpKey");
        
        setTimeout(() => {
          // Navigate to next page (dashboard, profile, etc.)
          navigate("/dashboard/profile");
        }, 2000);
      })
      .catch((e) => {
        setShowLoader(false);
        console.error('Verification error:', e.response?.data); // Debug log
        
        if (e.response?.data?.error) {
          setErrors(e.response.data.error);
        } else if (e.response?.data?.message) {
          setErrors(e.response.data.message);
        } else {
          languageValue === "En"
            ? setErrors("Invalid verification code. Please try again!")
            : setErrors("Code de vérification invalide. Veuillez réessayer!");
        }
      });
  };

  const handleResendOTP = () => {
    if (!canResend) return;
    
    setShowLoader(true);
    setErrors(null);
    
    // API endpoint based on verification type
    const RESEND_OTP_ENDPOINT = verificationType === 'phone' 
      ? "/send-sms-otp" // 🔥 UPDATED: Removed /api prefix to match your Laravel routes
      : "/api/resend-email-otp";
    

    const userEmail = localStorage.getItem("userEmail");  
    const requestData = verificationType === 'phone'
      ? { phone: phoneNumber, email: userEmail }  // Added email for phone OTP as well
      : { email: email };

    AXIOS_POST(
      RESEND_OTP_ENDPOINT,
      requestData,
      token,
      languageValue === "En" ? "en" : "fr"
    )
      .then((result) => {
        setShowLoader(false);
        languageValue === "En"
          ? toast.success("OTP sent successfully!")
          : toast.success("OTP envoyé avec succès!");
        
        // 🔥 NEW: Update otp_key from resend response
// Update otp_key from resend response
        if (result.data?.otp_key) {
          setCurrentOtpKey(result.data.otp_key);
          localStorage.setItem("verificationOtpKey", result.data.otp_key);
        }
        
        // Reset countdown
        setCanResend(false);
        setCountdown(60);
        
        // Clear current OTP
        setOtp(["", "", "", "", "", ""]);
        inputRefs.current[0]?.focus();
      })
      .catch((e) => {
        setShowLoader(false);
        if (e.response?.data?.error) {
          setErrors(e.response.data.error);
        } else if (e.response?.data?.message) {
          setErrors(e.response.data.message);
        } else {
          languageValue === "En"
            ? setErrors("Failed to resend OTP. Please try again!")
            : setErrors("Échec de l'envoi de l'OTP. Veuillez réessayer!");
        }
      });
  };

  const handleGoBack = () => {
    // Clear stored data when going back
    localStorage.removeItem("verificationOtpKey");
    
    if (verificationType === 'phone') {
      navigate("/phone-verification");
    } else {
      navigate("/resend-mail");
    }
  };

  return (
    <Container>
      <ToastContainer />
      <PageIntro>
        <ImgContainer src={cinfo.CimagePath} alt="logo" />
        <Title>
          <Translate Word="page.otpVerificationTitle" />
        </Title>
          <SubTitle>
            {verificationType === 'phone' 
              ? (languageValue === "En" 
                  ? `Enter the 6-digit code sent to ${phoneNumber}`
                  : `Entrez le code à 6 chiffres envoyé au ${phoneNumber}`)
              : (languageValue === "En"
                  ? `Enter the 6-digit code sent to your email`
                  : `Entrez le code à 6 chiffres envoyé à votre email`)
            }
          </SubTitle>
        
        {/* 🔥 NEW: Debug info (remove in production) */}

      </PageIntro>

      <OTPContainer>
        <OTPInputsWrapper>
          {otp.map((digit, index) => (
            <OTPInput
              key={index}
              ref={el => inputRefs.current[index] = el}
              type="text"
              maxLength="1"
              value={digit}
              onChange={(e) => handleInputChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
              isFocused={digit !== ""}
            />
          ))}
        </OTPInputsWrapper>

        {errors && <ErrorBox Message={errors} />}

        <SubmitButton 
          onClick={handleSubmit} 
          disabled={showLoader || otp.join('').length !== 6}
        >
          {showLoader ? <Loader /> : <Translate Word="page.verifyOTPButton" />}
        </SubmitButton>

      <ResendSection>
        <ResendText>
          <Translate Word="page.otpNotReceived" />{" "}
          <ResendLink 
            onClick={handleResendOTP}
            disabled={!canResend}
          >
            {canResend 
              ? <Translate Word="page.resendOTPButton" />
              : (languageValue === "En" 
                  ? `Resend in ${countdown}s` 
                  : `Renvoyer dans ${countdown}s`)
            }
          </ResendLink>
        </ResendText>
      </ResendSection>
      </OTPContainer>

      <AlternativeOptions>
        <BackLink onClick={handleGoBack}>
          ← <Translate Word="page.backTo" /> {verificationType === 'phone' 
              ? <Translate Word="page.phoneVerificationText" />
              : <Translate Word="page.emailVerificationText" />
            } <Translate Word="page.verificationText" />
        </BackLink>
      </AlternativeOptions>
<LanguageSwitch />
<Copyright>© {new Date().getFullYear()} Digimidi</Copyright>
      {/* <Copyright>© {new Date().getFullYear()} Digimidi</Copyright> */}
    </Container>
  );
};

export default OTPVerificationContainer;

// Styled Components (existing styles remain the same)
const Container = styled.div`
  position: relative;
  color: rgba(0, 0, 0, 0.85);
  max-width: 400px;
  margin: 0 auto;
  padding: 40px 20px;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  background-color: #f5f5f5;

  @media (max-width: 480px) {
    padding: 40px 15px;
  }

  @media (max-width: 360px) {
    padding: 40px 12px;
  }
`;

const ImgContainer = styled.img`
  width: 150px;
  object-fit: cover;
`;

const PageIntro = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-bottom: 30px;
  text-align: center;
`;

const Title = styled.h3`
  color: rgba(0, 0, 0, 0.85);
  font-weight: 700;
  margin-bottom: 10px;
  margin-top: 20px;
`;

const SubTitle = styled.p`
  color: rgba(0, 0, 0, 0.65);
  font-size: 14px;
  margin-bottom: 10px;
  line-height: 1.4;
`;

// 🔥 NEW: Debug info styling
const DebugInfo = styled.div`
  background: #f0f0f0;
  padding: 8px;
  border-radius: 4px;
  margin-top: 10px;
  font-family: monospace;
  
  small {
    display: block;
    color: #666;
    font-size: 11px;
  }
`;

const OTPContainer = styled.div`
  width: 100%;
  max-width: 100%;
  margin-bottom: 20px;
  overflow-x: hidden;
`;

const OTPInputsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 30px;
  width: 100%;
  max-width: 100%;

  @media (max-width: 480px) {
    gap: 6px;
  }

  @media (max-width: 360px) {
    gap: 4px;
  }
`;

const OTPInput = styled.input`
  flex: 1;
  max-width: 50px;
  height: 50px;
  text-align: center;
  font-size: 20px;
  font-weight: 600;
  border: 2px solid ${props => props.isFocused ? '#40a9ff' : '#e8e8e8'};
  border-radius: 8px;
  background-color: white;
  color: #333;
  outline: none;
  transition: all 0.3s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  box-sizing: border-box;
  
  &:focus {
    border-color: #40a9ff;
    box-shadow: 0 0 0 2px rgba(64, 169, 255, 0.2);
    background-color: white;
  }
  
  &:hover {
    border-color: #40a9ff;
    background-color: white;
  }

  @media (max-width: 480px) {
    max-width: 45px;
    height: 45px;
    font-size: 18px;
    border-radius: 6px;
  }

  @media (max-width: 360px) {
    max-width: 40px;
    height: 40px;
    font-size: 16px;
    border-radius: 6px;
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 12px 16px;
  height: 48px;
  cursor: pointer;
  color: #fff;
  background: ${props => props.disabled ? '#f5f5f5' : '#1890ff'};
  border-color: ${props => props.disabled ? '#d9d9d9' : '#1890ff'};
  font-weight: 500;
  font-size: 16px;
  border-radius: 6px;
  outline: none;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  margin: 30px 0 20px 0;

  &:hover:not(:disabled) {
    background: #40a9ff;
    border-color: #40a9ff;
  }

  &:disabled {
    background: #f5f5f5;
    border-color: #d9d9d9;
    color: rgba(0, 0, 0, 0.25);
    cursor: not-allowed;
  }
`;

const ResendSection = styled.div`
  text-align: center;
  margin: 20px 0;
`;

const ResendText = styled.p`
  color: rgba(0, 0, 0, 0.65);
  font-size: 14px;
  margin: 0;
`;

const ResendLink = styled.span`
  color: ${props => props.disabled ? 'rgba(0, 0, 0, 0.45)' : '#1890ff'};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  text-decoration: underline;
  
  &:hover {
    color: ${props => props.disabled ? 'rgba(0, 0, 0, 0.45)' : '#40a9ff'};
  }
`;

const AlternativeOptions = styled.div`
  text-align: center;
  margin: 20px 0;
`;

const BackLink = styled.p`
  color: #1890ff;
  cursor: pointer;
  font-size: 14px;
  margin: 0;
  
  &:hover {
    color: #40a9ff;
    text-decoration: underline;
  }
`;

const Copyright = styled.div`
  color: rgba(0, 0, 0, 0.45);
  margin-top: auto;
  text-align: center;
  font-size: 12px;
`;